import { useEffect, useMemo } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { getInboxView } from "../../utils";
import { useInboxThreadViewShallowStore } from "../../../../store";

interface ThreadData {
  id: string;
  type: string;
}

interface InboxThreadAutoSelectProps {
  threads: ThreadData[] | undefined;
}

/**
 * Handles two concerns for the inbox thread list:
 *
 * 1. **Auto-select redirect** — when no thread is selected and threads are
 *    loaded, renders a `<Navigate>` to either the most recently viewed thread
 *    (if still in the list) or the first thread. Using a `<Navigate>` component
 *    instead of an imperative `navigate()` call in a `useEffect` avoids a race
 *    condition: effects run after paint, so a `navigate()` in an effect can be
 *    superseded by other state changes (Zustand hydration, query re-evaluation)
 *    before React Router commits the route change.
 *
 * 2. **Session storage sync** — when a thread IS selected, syncs the selection
 *    to a sessionStorage-backed Zustand store so that returning to this view
 *    later can restore the last-viewed thread.
 */
export const InboxThreadAutoSelect = ({ threads }: InboxThreadAutoSelectProps) => {
  const location = useLocation();
  const view = getInboxView(location);
  const selectedThreadId = useParams().threadId;

  const { setInboxViewThreadData, inboxViewToThreadDataMap } = useInboxThreadViewShallowStore(
    "setInboxViewThreadData",
    "inboxViewToThreadDataMap"
  );
  const recentThreadData = view ? inboxViewToThreadDataMap[view] : undefined;

  // Derive the thread data that should be persisted to session storage.
  // Computed during render so the effect below only performs the external store write.
  const threadDataToSync = useMemo(() => {
    if (location.pathname.endsWith("/new")) return null;
    if (!selectedThreadId || !view) return null;
    const selectedThread = threads?.find(({ id }) => id === selectedThreadId);
    if (!selectedThread || selectedThreadId === recentThreadData?.threadId) return null;
    return { view, type: selectedThread.type.toLowerCase(), threadId: selectedThread.id };
  }, [location.pathname, recentThreadData?.threadId, selectedThreadId, threads, view]);

  // Sync the currently-selected thread to the sessionStorage-backed Zustand store
  // so that returning to this view later can restore the last-viewed thread.
  // useEffect is justified here because we're syncing React state to an external store.
  useEffect(() => {
    if (!threadDataToSync) return;
    setInboxViewThreadData(threadDataToSync.view, {
      type: threadDataToSync.type,
      threadId: threadDataToSync.threadId,
    });
  }, [setInboxViewThreadData, threadDataToSync]);

  // Compute the auto-select redirect URL declaratively during render.
  // When no thread is selected and threads are loaded, we redirect to either
  // the most recently viewed thread (if still in the list) or the first thread.
  const autoSelectRedirectUrl = useMemo(() => {
    if (location.pathname.endsWith("/new")) return null;
    if (selectedThreadId || !view || !threads?.length) return null;

    const recentThreadIdIsInList = recentThreadData
      ? threads.some(({ id }) => id === recentThreadData.threadId)
      : false;

    if (recentThreadIdIsInList && recentThreadData) {
      return `/messaging/${view}/${recentThreadData.type.toLowerCase()}/${recentThreadData.threadId}${location.search}`;
    }

    const firstThread = threads.at(0);
    if (firstThread) {
      return `/messaging/${view}/${firstThread.type.toLowerCase()}/${firstThread.id}${location.search}`;
    }

    return null;
  }, [location.pathname, location.search, recentThreadData, selectedThreadId, threads, view]);

  if (autoSelectRedirectUrl) {
    return <Navigate replace to={autoSelectRedirectUrl} />;
  }

  return null;
};
